import { ReactElement } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Skeleton } from 'src/components/ui/skeleton';
import type { TimeSeriesDataPoint } from 'src/redux/admin/reporting/reporting.types';
import { SurveyGroupTrendsLegendRows } from './survey-group-trends-metric-legend';

export interface FacetLineMetric {
  key: string;
  name: string;
  color: string;
  imageUrl?: string;
}

interface SurveyGroupTrendsFacetLineChartProps {
  data: TimeSeriesDataPoint[];
  metrics: FacetLineMetric[];
  isLoading: boolean;
  /** Shared 0–100 scale so panels are comparable */
  yDomain?: [number, number];
}

function formatDateShort(iso: string): string {
  return new Date(`${iso.split('T')[0]}T00:00:00`).toLocaleDateString('en-GB', {
    month: 'short',
    day: 'numeric',
  });
}

function formatDateFull(iso: string): string {
  return new Date(`${iso.split('T')[0]}T00:00:00`).toLocaleDateString('en-GB', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Single-axis multi-line chart for one survey’s answer trends (percent scale).
 * Avoids MetricChart’s dual-Y behaviour when there are more than two series.
 */
const SurveyGroupTrendsFacetLineChart = ({
  data,
  metrics,
  isLoading,
  yDomain = [0, 100],
}: SurveyGroupTrendsFacetLineChartProps): ReactElement => {
  if (isLoading) {
    return <Skeleton className="h-[220px] w-full rounded-lg" />;
  }

  if (!metrics.length || !data.length) {
    return (
      <div className="flex h-[180px] items-center justify-center rounded-lg border border-dashed border-gray-200 text-sm text-gray-500">
        No points in range
      </div>
    );
  }

  const hasLegendImages = metrics.some((m) => Boolean(m.imageUrl));

  return (
    <div className="h-[240px] w-full min-h-[200px]">
      <ResponsiveContainer
        width="100%"
        height="100%"
      >
        <LineChart
          data={data}
          margin={{ top: 6, right: 8, left: 0, bottom: 4 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#e5e7eb"
          />
          <XAxis
            dataKey="date"
            tickFormatter={formatDateShort}
            tick={{ fontSize: 11, fill: '#64748b' }}
            minTickGap={28}
            stroke="#cbd5e1"
          />
          <YAxis
            domain={yDomain}
            tickFormatter={(v) => `${v}%`}
            tick={{ fontSize: 11, fill: '#64748b' }}
            width={40}
            stroke="#cbd5e1"
          />
          <Tooltip
            labelFormatter={(label) => formatDateFull(String(label))}
            formatter={(value: number, name: string) => [`${typeof value === 'number' ? value.toFixed(1) : value}%`, name]}
            contentStyle={{ borderRadius: 8, fontSize: 12 }}
          />
          <Legend
            wrapperStyle={{ fontSize: 11, paddingTop: 8, width: '100%' }}
            iconType="line"
            {...(hasLegendImages
              ? { content: () => <SurveyGroupTrendsLegendRows metrics={metrics} /> }
              : {})}
          />
          {metrics.map((m) => (
            <Line
              key={m.key}
              type="monotone"
              dataKey={m.key}
              name={m.name}
              stroke={m.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SurveyGroupTrendsFacetLineChart;
